// Register write log → per-tick SID state (one snapshot per play call / frame).

import type { Capture, RawFrames, RawVoice } from './types';

/** SID oscillator frequency in Hz for a 16-bit frequency register value. */
export function sidFreqToHz(reg: number, clockHz: number): number {
  return (reg * clockHz) / 16777216;
}

/** Fractional MIDI note number for a frequency (A4 = 440 Hz = note 69). NaN for 0 Hz. */
export function hzToMidi(hz: number): number {
  return hz > 0 ? 69 + 12 * Math.log2(hz / 440) : NaN;
}

export function sidFreqToMidi(reg: number, clockHz: number): number {
  return hzToMidi(sidFreqToHz(reg, clockHz));
}

/** Nearest MIDI note plus residual in cents. */
export function nearestNote(midi: number): { note: number; cents: number } {
  const note = Math.round(midi) || 0; // avoid -0
  return { note, cents: (midi - note) * 100 };
}

const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export function noteName(note: number): string {
  return `${NAMES[((note % 12) + 12) % 12]}${Math.floor(note / 12) - 1}`;
}

function voice(n: number): RawVoice {
  return {
    freq: new Uint16Array(n),
    pw: new Uint16Array(n),
    ctrl: new Uint8Array(n),
    ad: new Uint8Array(n),
    sr: new Uint8Array(n),
    gateRise: new Uint8Array(n),
    ctrlSeen: new Uint8Array(n),
  };
}

export function decodeCapture(c: Capture): RawFrames {
  const n = c.ticks;
  const voices: [RawVoice, RawVoice, RawVoice] = [voice(n), voice(n), voice(n)];
  const cutoff = new Uint16Array(n);
  const resFilt = new Uint8Array(n);
  const modeVol = new Uint8Array(n);
  const regs = new Uint8Array(0x19);
  const rise = [0, 0, 0];
  const seen = [0, 0, 0];
  let w = 0;

  for (let t = 0; t < n; t++) {
    rise[0] = rise[1] = rise[2] = 0;
    seen[0] = seen[1] = seen[2] = 0;
    while (w < c.writeCount && c.writeTick[w] <= t) {
      const r = c.writeReg[w];
      const v = c.writeValue[w];
      if (r === 4 || r === 11 || r === 18) {
        const vi = (r - 4) / 7;
        seen[vi] |= v;
        if (v & 1 && !(regs[r] & 1)) rise[vi] = 1;
      }
      regs[r] = v;
      w++;
    }
    for (let vi = 0; vi < 3; vi++) {
      const b = vi * 7;
      const out = voices[vi];
      out.freq[t] = regs[b] | (regs[b + 1] << 8);
      out.pw[t] = (regs[b + 2] | (regs[b + 3] << 8)) & 0xfff;
      out.ctrl[t] = regs[b + 4];
      out.ad[t] = regs[b + 5];
      out.sr[t] = regs[b + 6];
      out.gateRise[t] = rise[vi];
      out.ctrlSeen[t] = seen[vi] | regs[b + 4];
    }
    cutoff[t] = (regs[0x15] & 7) | (regs[0x16] << 3);
    resFilt[t] = regs[0x17];
    modeVol[t] = regs[0x18];
  }
  return { ticks: n, tickRate: c.tickRate, clockHz: c.clockHz, voices, cutoff, resFilt, modeVol };
}

/** Per-tick fractional MIDI pitch for one voice; NaN when the gate is off. */
export function rawPitchTrace(raw: RawFrames, vi: number): Float32Array {
  const v = raw.voices[vi];
  const out = new Float32Array(raw.ticks);
  for (let t = 0; t < raw.ticks; t++) {
    out[t] = v.ctrl[t] & 1 ? sidFreqToMidi(v.freq[t], raw.clockHz) : NaN;
  }
  return out;
}
